import type {
  ClientPostChangeConfirmation,
  ClientPostCreateConfirmation
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { ClientPostTypeEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import { zodResolver } from '@hookform/resolvers/zod';
import { useIsMutating } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useThrottledCallback } from 'use-debounce';

import { POST_MUTATION_KEYS } from '@/features/api/constants/mutation';
import { useGetOrganizationById } from '@/features/api/hooks/grapevine-organization/useGetOrganizationById';
import { useMediaPlaceholders } from '@/features/media/hooks/useMediaPlaceholder';
import { EMPTY_POST_FORM } from '@/features/post-editor/constants/postForm';
import { useCreatePost } from '@/features/post-editor/hooks/useCreatePost';
import { useEditorModal } from '@/features/post-editor/hooks/useEditorModal';
import { useGetPostTypeFeature } from '@/features/post-editor/hooks/useGetPostFeature';
import { useOrganizationPostConfirmation } from '@/features/post-editor/hooks/useOrganizationPostConfirmation';
import {
  createOrganizationPostFormSchema,
  createPersonPostFormSchema
} from '@/features/post-editor/schemas/postForm';
import type {
  PostCreationChannelOption,
  PostCreationContext
} from '@/features/post-editor/types/postCreationOptions';
import type { PostFormValues } from '@/features/post-editor/types/postForm';
import {
  getAllowedMarketPostTypes,
  getSenderChannelTypes
} from '@/features/post-editor/utils/postCreationOptions';
import { resolveAllowedChannel } from '@/features/post-editor/utils/postFormReset';
import { PostFeedType } from '@/features/posts/types/postFeed';
import type { Sender } from '@/features/sender-selection/types/sender';
import { SenderType } from '@/features/sender-selection/types/sender';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { isEmptyOrNull } from '@/shared/utils/string';

export type UsePostCreationFormParams = {
  sender: Sender;
  creationContext: PostCreationContext;
  senderChannels?: PostCreationChannelOption[];
  groupId?: string;
  defaultGeoAreaIds?: string[];
  autoSelectOrganizationGeoArea?: boolean;
  params?: { channelType?: PostFeedType; groupId?: string };
  onSuccess: (
    post: ClientPostCreateConfirmation | ClientPostChangeConfirmation
  ) => void;
};

export const usePostCreationForm = ({
  sender,
  creationContext,
  senderChannels,
  groupId,
  defaultGeoAreaIds,
  autoSelectOrganizationGeoArea = false,
  params,
  onSuccess
}: UsePostCreationFormParams) => {
  const { t } = useTranslation(LocalizationNameSpace.POST_CREATION, {
    keyPrefix: 'screens.post-editor'
  });

  const isOrganization = sender.type === SenderType.ORGANIZATION;
  const organizationId = isOrganization ? sender.organizationId : undefined;
  const { data: organization } = useGetOrganizationById(organizationId);

  const resolvedDefaultGeoAreaIds = useMemo(() => {
    if (defaultGeoAreaIds !== undefined) {
      return defaultGeoAreaIds;
    }
    if (
      autoSelectOrganizationGeoArea &&
      isOrganization &&
      organization?.geoAreaIds
    ) {
      return organization.geoAreaIds.filter(
        (id): id is string => typeof id === 'string' && id.length > 0
      );
    }
    return;
  }, [
    defaultGeoAreaIds,
    autoSelectOrganizationGeoArea,
    isOrganization,
    organization?.geoAreaIds
  ]);

  const requestedChannelType = isEmptyOrNull(groupId)
    ? (params?.channelType ?? PostFeedType.GOSSIP)
    : PostFeedType.GOSSIP;

  const availableChannels = useMemo(
    () => getSenderChannelTypes(senderChannels ?? []),
    [senderChannels]
  );

  const initialChannelType = resolveAllowedChannel(
    requestedChannelType,
    availableChannels
  );

  const postTypeFeature = useGetPostTypeFeature(initialChannelType);

  const postFormResolver = useMemo<Resolver<PostFormValues>>(
    () =>
      isOrganization
        ? zodResolver(createOrganizationPostFormSchema({ t, postTypeFeature }))
        : zodResolver(createPersonPostFormSchema({ t, postTypeFeature })),
    [isOrganization, t, postTypeFeature]
  );

  const form = useForm<PostFormValues>({
    resolver: postFormResolver,
    mode: 'onChange',
    defaultValues: {
      ...EMPTY_POST_FORM,
      channelType: initialChannelType,
      marketType:
        getAllowedMarketPostTypes(senderChannels ?? [])[0] ??
        ClientPostTypeEnum.Offer,
      geoAreaIds: resolvedDefaultGeoAreaIds
    }
  });
  const { handleSubmit, formState, setValue, getValues } = form;

  const initializedGeoAreaIds = useRef(false);
  useEffect(() => {
    if (
      !initializedGeoAreaIds.current &&
      resolvedDefaultGeoAreaIds &&
      resolvedDefaultGeoAreaIds.length > 0 &&
      (!getValues('geoAreaIds') || getValues('geoAreaIds')?.length === 0)
    ) {
      initializedGeoAreaIds.current = true;
      setValue('geoAreaIds', resolvedDefaultGeoAreaIds, {
        shouldValidate: true
      });
    }
  }, [resolvedDefaultGeoAreaIds, setValue, getValues]);

  const modal = useEditorModal();

  const media = useMediaPlaceholders({
    initialImages: [],
    initialDocuments: []
  });

  const createPost = useCreatePost({
    creationContext,
    groupId,
    organizationId,
    senderChannels: senderChannels ?? (isOrganization ? undefined : []),
    temporaryMediaItemIds: media.temporaryMediaItemIds,
    temporaryDocumentItemIds: media.temporaryDocumentItemIds,
    onSuccess
  });

  const isCreating =
    useIsMutating({ mutationKey: [POST_MUTATION_KEYS.CREATE] }) > 0;

  const submit = useCallback(() => {
    handleSubmit(createPost)();
  }, [handleSubmit, createPost]);

  const { confirmAndSubmit } = useOrganizationPostConfirmation({
    organizationId,
    form,
    onConfirm: submit
  });

  const handleSubmitThrottled = useThrottledCallback(confirmAndSubmit, 1000);

  const isSubmitDisabled =
    !formState.isValid || media.isUploadPending || isCreating;

  return {
    form,
    media,
    modal,
    isCreating,
    isSubmitDisabled,
    submit: handleSubmitThrottled
  };
};
